/**
 * QueuedFactDisplay
 * Inline component (not modal) that displays encouraging facts in the chat flow
 * Shows prominently with user-controlled dismissal via Continue button
 */

import React, { useState, useEffect } from 'react';
import styles from '../styles/QueuedFactDisplay.module.css';

function QueuedFactDisplay({ isStillLoading, onDismiss }) {
  const [fact, setFact] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDismissing, setIsDismissing] = useState(false);

  // Fetch queued fact on mount
  useEffect(() => {
    async function fetchFact() {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setIsLoading(false);
          return;
        }

        const response = await fetch('/api/loading-screen/check-queued', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          console.error('[QueuedFactDisplay] Failed to fetch queued fact:', response.status);
          setIsLoading(false);
          return;
        }

        const data = await response.json();

        if (data.hasFact) {
          setFact({
            id: data.factId,
            text: data.message,
            type: data.factType
          });
        }

        setIsLoading(false);

      } catch (error) {
        console.error('[QueuedFactDisplay] Error fetching queued fact:', error);
        setIsLoading(false);
      }
    }

    fetchFact();
  }, []);

  // Mark fact as displayed and dismiss
  const handleContinue = async () => {
    if (!fact) {
      onDismiss();
      return;
    }

    setIsDismissing(true);

    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        // Mark as displayed on backend
        await fetch(`/api/loading-screen/mark-displayed/${fact.id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('[QueuedFactDisplay] Error marking fact as displayed:', error);
      // Continue with dismissal even if marking fails
    }

    // Animate out
    setTimeout(() => {
      onDismiss();
    }, 300);
  };

  // Don't render if loading or no fact
  if (isLoading || !fact) {
    return null;
  }

  // Determine icon based on fact type
  const getIcon = () => {
    switch (fact.type) {
      case 'celebration':
        return '🎉';
      case 'gentle_nudge':
        return '💪';
      case 'insight':
        return '💡';
      case 'milestone':
        return '🏆';
      default:
        return '✨';
    }
  };

  // Determine title based on fact type
  const getTitle = () => {
    switch (fact.type) {
      case 'celebration':
        return 'Celebrate!';
      case 'gentle_nudge':
        return 'Keep Going!';
      case 'milestone':
        return 'Milestone Reached!';
      default:
        return 'Did You Know?';
    }
  };

  return (
    <div className={`${styles.container} ${isDismissing ? styles.fadeOut : ''}`}>
      <div className={styles.iconContainer}>
        <span className={styles.icon}>{getIcon()}</span>
      </div>

      <div className={styles.content}>
        <h2 className={styles.title}>{getTitle()}</h2>
        <p className={styles.factText}>{fact.text}</p>

        {/* Show loading dots while still loading */}
        {isStillLoading && (
          <div className={styles.loadingDots}>
            <div className={styles.dot}></div>
            <div className={styles.dot}></div>
            <div className={styles.dot}></div>
          </div>
        )}
      </div>

      <button
        className={styles.continueButton}
        onClick={handleContinue}
        disabled={isDismissing || isStillLoading}
      >
        {isStillLoading ? 'Loading...' : 'Continue'}
      </button>
    </div>
  );
}

export default QueuedFactDisplay;
